import { useEffect, useMemo, useState } from 'react';

import { ConfigProvider } from 'antd';
import { connect, useSelector } from 'react-redux';
import { BrowserRouter as Router, Switch, Redirect } from 'react-router-dom';
import SockJsClient from 'react-stomp';

import { staticRoutes } from './Routes';
import { withSmartChargingSimulator } from './smartChargingSimulatorRoute';

import Header from '../components/molecules/Header/Header';
import MainContent from '../components/templates/MainContent/MainContent';
import { getPublicAndOwnedChargePoints } from '../hooks/chargepoints';
import { getNotifications } from '../hooks/notifications';
import {
  setPlugShareOriginChargePoints,
  setChargePoints,
  setIsUserAuthenticated,
  setNotifications,
  setRoutes,
  setAllOriginChargePoints,
  setChargePoint,
  addFilteredNotification,
  addFilteredUserNotification,
  addNotification,
  setChargePointConnector,
  setWebsocketClient,
  setOcppTags,
} from '../redux/actions';
import { getToken } from '../services/authentication';
import { webSocketApiUrlsDictionary } from '../constants';
import { getOcppTags } from '../hooks/ocpptags';
import { isMonetizationProUser } from '../utils/isMonetizationProUser/isMonetizationProUser';
import { isVoucherProUser } from '../utils/isVoucherProUser/isVoucherProUser';
import { isProUser } from '../utils/isProUser/isProUser';
import { isTerpelTenant } from '../utils/isTerpelTenant/isTerpelTenant';

const API_KEY = localStorage.getItem('api-key');

const defaultUrl =
  process.env.REACT_APP_WEBSOCKET_URL || `${window.location.origin}/ws`;

const theme = localStorage.getItem('theme');

const BACKEND_WEBSOCKET_URL =
  webSocketApiUrlsDictionary[theme]?.wsUrl || defaultUrl;

function showNotification(type) {
  if(process.env.REACT_APP_LOG && process.env.REACT_APP_LOG === 'DEBUG'){
    return true;
  }
  const notifyTypes = ['TransactionStarted', 'TransactionEnded', 'Failure', 'StationBooted', 'Warning'];
  return notifyTypes.includes(type);
}

const AppRouter = (props) => {
  const {
    antdLibLanguage,
    user: { isUserAuthenticated, routes },
    dispatch,
  } = props;

  const [clientRef, setClientRef] = useState({});
  const [isConnected, setIsConnected] = useState(false);

  const authToken = getToken();

  const user = JSON.parse(localStorage.getItem('auth-user'));

  const userId = user?.id;

  const stations = useSelector(
    (state) => state?.chargePoints?.chargePoints?.chargePointList ?? undefined,
  );

  if (isConnected && clientRef?._subscribe) {
    dispatch(setWebsocketClient(clientRef));
  }

  if (authToken !== null && !isUserAuthenticated) {
    if (window.location.pathname.indexOf('/auth/') > -1) {
      document.location.href = '/';
    }

    dispatch(setIsUserAuthenticated(true));

    let mappedRoutes = user?.permissions?.map(({ route }) => {
      const mappedStaticRoute = staticRoutes.find(({ path }) => path === route);

      return mappedStaticRoute || false;
    });

    mappedRoutes = withSmartChargingSimulator(mappedRoutes, staticRoutes);

    if (!isMonetizationProUser() && !isTerpelTenant()) {
      mappedRoutes = mappedRoutes?.filter(
        (route) =>
          !route?.path?.startsWith('/dynamic-pricing') &&
          !route?.path?.startsWith('/virtual-group') &&
          !route?.path?.startsWith('/financial'),
      );
    }

    if (!isVoucherProUser() && !isTerpelTenant()) {
      mappedRoutes = mappedRoutes?.filter(
        (route) => !route?.path?.startsWith('/voucher'),
      );
    }

    if (!isProUser()) {
      mappedRoutes = mappedRoutes?.filter(
        (route) => !route?.path?.startsWith('/log'),
      );
    }

    dispatch(setRoutes(mappedRoutes));
  }

  useEffect(() => {
    (async function () {
      if (authToken !== null && isUserAuthenticated && userId) {
        const notifications = await getNotifications();
        const notificationList = notifications?.notificationList?.filter(notification => showNotification(notification.type))
        dispatch(setNotifications({ notificationList }));

        dispatch(
          setPlugShareOriginChargePoints(
            await getPublicAndOwnedChargePoints(userId, 'PLUG_SHARE'),
          ),
        );

        dispatch(
          setChargePoints(await getPublicAndOwnedChargePoints(userId, 'MOVE')),
        );

        dispatch(setOcppTags(await getOcppTags()));
      }
    })();
  }, [authToken, dispatch, isUserAuthenticated, userId]);

  useEffect(() => {
    if (isConnected && clientRef?._subscribe) {
      setTimeout(() => {
        stations?.length &&
          stations.forEach((station) => {
            clientRef._subscribe(
              `/topic/status/chargeBox/${station?.chargeBoxId}`,
            );
            if (station?.connectors?.length)
              station.connectors.forEach((connector) => {
                clientRef._subscribe(
                  `/topic/status/chargeBox/${station?.chargeBoxId}/connector/${connector.connectorId}`,
                );
              });
          });
      }, 5000);
    }
  }, [stations, clientRef, isConnected]);

  useEffect(() => {
    (async function () {
      if (isUserAuthenticated && stations?.length) {
        dispatch(
          setAllOriginChargePoints({
            error: null,
            chargePointList: [...stations],
          }),
        );
      }
    })();
  }, [isUserAuthenticated, dispatch, stations]);

  const onMessageReceive = async (msg, topic) => {
    if (topic === '/topic/' + API_KEY + '/notification/chargeBox' && showNotification(msg?.type)) {
      dispatch(addNotification(msg));
      dispatch(addFilteredNotification(msg, msg.chargeBoxId));
      if (msg.userPk) dispatch(addFilteredUserNotification(msg, msg.userPk));
    }

    if (topic === '/topic/' + API_KEY + '/chargeBox') {
      if (msg.type === 'update') {
        dispatch(setChargePoint(msg.chargePoint, msg.chargePoint.chargeBoxPk));
      } else if (msg.type === 'insert') {
        dispatch(
          setChargePoints(await getPublicAndOwnedChargePoints(userId, 'MOVE')),
        );
      }
    }

    if (topic.includes('chargeBox') && topic.includes('connector')) {
      dispatch(setChargePointConnector(msg.chargeBoxId, msg.connectorId, msg));
    }
  };

  const handleReactUrl = useMemo(() => {
    const { pathname } = window.location;

    const checkIfUrlNumbers = staticRoutes.find(
      (route) => route.path === pathname.replace(/\d+/g, ':id'),
    );

    const checkIfUrlQrCode = new RegExp('/qrcode/start/.*/.*').test(pathname);

    const uuidRegex =
      /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/;

    const checkIfUrlUuid = staticRoutes.find(
      (route) => route.path === pathname.replace(uuidRegex, ':id'),
    );

    const weekRegex =
      /\b((mon|tues|wed(nes)?|thur(s)?|fri|sat(ur)?|sun)(day)?|exception)\b/;

    const checkIfUrlWeekDay = staticRoutes.find(
      (route) =>
        route.path ===
        pathname.replace(uuidRegex, ':id').replace(weekRegex, ':weekday'),
    );

    if (
      !checkIfUrlNumbers &&
      !checkIfUrlQrCode &&
      !checkIfUrlUuid &&
      !checkIfUrlWeekDay
    ) {
      return <Redirect to='/not-found' />;
    }
  }, []);

  return (
    <ConfigProvider locale={antdLibLanguage}>
      <Router>
        {!isUserAuthenticated &&
          !authToken &&
          window.location.href.indexOf('auth') === -1 &&
          window.location.href.indexOf('qrcode') === -1 &&
          window.location.href.indexOf('terms-of-use') === -1 &&
          window.location.href.indexOf('not-found') === -1 &&
          window.location.href.indexOf('three-ds-authentication') === -1 ? (
          <Redirect to='/auth/login' />
        ) : null}
        {isUserAuthenticated && authToken ? <Header /> : null}
        {isUserAuthenticated && authToken ? (
          <SockJsClient
            url={BACKEND_WEBSOCKET_URL}
            topics={[
              '/topic/' + API_KEY + '/notification/chargeBox',
              '/topic/' + API_KEY + '/stats',
              '/topic/' + API_KEY + '/chargeBox',
            ]}
            onConnect={() => {
              setIsConnected(true);
            }}
            headers={{ Authorization: authToken, Platform: 'DASHBOARD' }}
            onMessage={onMessageReceive}
            ref={(client) => {
              setClientRef(client);
            }}
          />
        ) : null}

        {handleReactUrl}

        <Switch>
          <MainContent {...{ isUserAuthenticated, routes, authToken }} />
        </Switch>
      </Router>
    </ConfigProvider>
  );
};

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps)(AppRouter);
